import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, Building2, Linkedin, Handshake, Download } from "lucide-react";
import { toast } from "sonner";

const statusBadge = (s) => ({ new: "bg-nhs-blue/10 text-nhs-blue", reviewing: "bg-nhs-amber/20 text-[#8a6400]", approved: "bg-nhs-teal/15 text-nhs-teal", declined: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-700");
const STATUSES = ["new", "reviewing", "approved", "declined"];

function Field({ label, value }) {
    if (!value) return null;
    return <div className="mt-2"><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</p><p className="text-sm text-slate-700 mt-0.5">{value}</p></div>;
}

export default function Partners() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const load = () => api.get("/partners").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try { await api.patch(`/partners/${id}?status=${status}`); toast.success("Updated"); load(); }
        catch { toast.error("Update failed"); }
    };
    const shown = filter === "all" ? items : items.filter((i) => i.status === filter);

    const exportCsv = () => downloadCSV(`partnership-inquiries-${new Date().toISOString().slice(0, 10)}.csv`, shown, [
        { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
        { key: "role_title", label: "Role / Title" }, { key: "organisation", label: "Organisation" },
        { key: "profession", label: "Background" }, { key: "experience", label: "Experience" },
        { key: "qualifications", label: "Qualifications" }, { key: "linkedin", label: "LinkedIn" },
        { key: "lived_experience", label: "Lived experience" }, { key: "contribution", label: "Could contribute" },
        { key: "interest", label: "Why interested" }, { key: "status", label: "Status" }, { key: "created_at", label: "Submitted" },
    ]);

    return (
        <div className="space-y-6" data-testid="partners-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Partnership Inquiries</h1>
                    <p className="text-slate-500 mt-1">Leaders, coaches and specialists applying to contribute to EDI Connect.</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="gap-2" onClick={exportCsv} disabled={shown.length === 0} data-testid="partner-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-40 bg-white" data-testid="partner-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {shown.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3" data-testid="empty-partners">
                    <Handshake className="h-8 w-8" /> No partnership inquiries yet.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    {shown.map((p) => (
                        <div key={p.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`partner-${p.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-heading font-semibold text-lg text-nhs-ink">{p.name}</h3>
                                    <p className="text-sm text-slate-500">{p.role_title || "—"}{p.organisation ? ` · ${p.organisation}` : ""}</p>
                                </div>
                                <Badge className={`border-0 capitalize ${statusBadge(p.status)}`}>{p.status}</Badge>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                                <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" /> {p.email}</span>
                                {p.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-slate-400" /> {p.phone}</span>}
                                {p.linkedin && <a href={p.linkedin.startsWith("http") ? p.linkedin : `https://${p.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-nhs-blue"><Linkedin className="h-4 w-4" /> LinkedIn</a>}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {p.profession && <Badge className="bg-slate-100 text-slate-600 border-0">{p.profession}</Badge>}
                                {p.experience && <Badge className="bg-slate-100 text-slate-600 border-0">{p.experience}</Badge>}
                            </div>
                            <Field label="Qualifications" value={p.qualifications} />
                            <Field label="Lived experience" value={p.lived_experience} />
                            <Field label="Could contribute" value={p.contribution} />
                            <Field label="Why interested" value={p.interest} />
                            <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4">
                                <span className="text-xs text-slate-400">Status:</span>
                                <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                                    <SelectTrigger className="h-8 w-36" data-testid={`partner-status-${p.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
